import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type AuthTokens,
  type CompleteOnboardingInput,
  type OrgJoinPending,
  type AuthenticatedUser,
  type LoginInput,
  type RegisterInput,
} from '@apm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { refreshSession } from '../../lib/http';
import { tokenStore } from '../../lib/token-store';
import { authApi } from './api';

export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous';

interface AuthValue {
  status: AuthStatus;
  user: AuthenticatedUser | null;
  login: (input: LoginInput) => Promise<void>;
  /** Tạo tổ chức mới cùng người dùng đầu tiên, rồi vào luôn — APM-AUTH-h. */
  register: (input: RegisterInput) => Promise<void>;
  /** Đăng nhập vào một tài khoản trưng bày, không cần mật khẩu — APM-1318a. */
  loginAsDemo: (userId: string) => Promise<void>;
  /**
   * Đổi `initData` của Telegram Mini App lấy phiên — APM-TG-i.
   *
   * Đi qua `adopt` như mọi đường đăng nhập khác, nên cache React Query được dọn
   * đúng một lần và token nằm đúng chỗ token vẫn nằm.
   */
  loginWithTelegram: (initData: string) => Promise<void>;
  /**
   * Đổi phiếu onboarding của Google lấy tài khoản thật — APM-GAUTH-a.
   * `OrgJoinPending` khi chọn thẻ 3 với link tổ chức cần duyệt (APM-PSPACE-m): đơn đã gửi,
   * chưa có phiên nào được mở. Mọi lựa chọn khác mở phiên và trả `null`.
   */
  completeOnboarding: (input: CompleteOnboardingInput) => Promise<OrgJoinPending | null>;
  /**
   * Phòng chờ: email trên phiếu đã có tài khoản (vừa được mời / duyệt) ⇒ đổi phiếu lấy
   * phiên, không bấm Google lại — APM-GAUTH-e.
   */
  enterFromOnboarding: () => Promise<void>;
  logout: () => Promise<void>;
  /** true khi phiên vừa kết thúc ngoài ý muốn — dùng để hiện thông báo. */
  expired: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Khôi phục phiên chạy một lần mỗi lần tải trang, kể cả khi React 19 StrictMode
 * gắn effect hai lần trong dev.
 *
 * Chống gọi `/auth/refresh` hai lần thì `refreshSession()` ở `http.ts` đã lo
 * (single-flight) — và đó mới là chỗ quan trọng, vì refresh token xoay mỗi lần
 * dùng nên lời gọi thứ hai sẽ bị server coi là token bị đánh cắp. Cache ở đây
 * chỉ thêm việc bỏ luôn lời gọi `/auth/me` thừa.
 *
 * ⚠️ Nhưng phải cache **promise**, tuyệt đối không phải một cờ boolean. Với cờ
 * boolean thì lần mount thứ nhất của StrictMode bật cờ rồi bị huỷ, lần mount
 * thứ hai thấy cờ đã bật nên thoát sớm mà không ai gán state — ứng dụng treo
 * vĩnh viễn ở màn hình "đang khôi phục phiên". Cache promise thì lần mount thứ
 * hai chờ đúng promise cũ và nhận được kết quả.
 */
let bootRestore: Promise<AuthenticatedUser | null> | null = null;

function restoreSession(): Promise<AuthenticatedUser | null> {
  bootRestore ??= (async () => {
    const token = await refreshSession();
    if (!token) return null;

    try {
      return await authApi.me();
    } catch {
      // Refresh được nhưng /auth/me hỏng: token vô nghĩa, đừng giữ lại.
      tokenStore.set(null);
      return null;
    }
  })();

  return bootRestore;
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [status, setStatus] = useState<AuthStatus>('restoring');
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [expired, setExpired] = useState(false);
  const statusRef = useRef(status);
  statusRef.current = status;
  // `AuthProvider` nằm **trong** `QueryClientProvider` (xem `main.tsx`), nên
  // chỗ này đọc được cache để dọn khi danh tính đổi.
  const queryClient = useQueryClient();

  // ── Khôi phục phiên lúc mở trang ──────────────────────────────────────────
  //
  // Access token nằm trong memory nên reload là mất. Refresh cookie thì không:
  // đổi nó lấy access token mới ngay lúc khởi động, để F5 không đá người dùng
  // về màn hình đăng nhập.
  useEffect(() => {
    let cancelled = false;

    void restoreSession().then((me) => {
      if (cancelled) return;
      setUser(me);
      setStatus(me ? 'authenticated' : 'anonymous');
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // ── Token bị xoá ở tầng http ⇒ phiên kết thúc ────────────────────────────
  //
  // `http.ts` xoá token khi refresh thất bại. Lắng nghe ở đây thay vì để
  // `http.ts` gọi ngược vào React: tầng mạng không nên biết gì về component.
  useEffect(
    () =>
      tokenStore.subscribe((next) => {
        if (next !== null) return;
        if (statusRef.current !== 'authenticated') return;

        setUser(null);
        setStatus('anonymous');
        setExpired(true);
      }),
    [],
  );

  /**
   * Gán state sau khi có token — dùng chung cho đăng nhập thường và demo.
   *
   * ## ⚠️ Đổi danh tính phải dọn cache, và khi đang đăng nhập thì phải NẠP LẠI
   *
   * React Query giữ cache theo khoá query, **không** theo người đang đăng nhập.
   * Đổi người mà không dọn thì `abilities`, danh sách dự án, việc, báo cáo…
   * của người trước ở lại nguyên trên màn hình — triệu chứng đúng như đã gặp:
   * đổi vai xong vẫn thấy quyền cũ cho tới khi bấm F5.
   *
   * Và đây không chỉ là chuyện giao diện cũ: trên một máy dùng chung, dữ liệu
   * dự án của người trước hiện ra dưới phiên của người sau.
   *
   * Khi **đang** có phiên (đổi vai, đổi tài khoản demo) thì nạp lại cả trang
   * thay vì `queryClient.clear()`: những request đã bay đi mang token **cũ**,
   * và câu trả lời của chúng về sau vẫn ghi vào đúng khoá query ấy — dọn cache
   * xong vẫn có thể bị dữ liệu cũ ghi lên. Cùng lý lẽ và cùng cách xử lý với
   * `OrgSwitcher`; token nằm trong memory nên nó mất theo lượt nạp lại, còn
   * phiên sống tiếp nhờ cookie refresh vừa được ghi.
   *
   * Lần đăng nhập đầu (đang `anonymous`) thì không có gì để đua: chỉ dọn cache,
   * để không phải nạp lại trang ngay sau khi vừa vào.
   */
  const adopt = useCallback(
    (tokens: AuthTokens) => {
      const switching = statusRef.current === 'authenticated';

      tokenStore.set(tokens.accessToken);
      setUser(tokens.user);
      setExpired(false);
      setStatus('authenticated');

      if (switching) {
        window.location.reload();
        return;
      }
      queryClient.clear();
    },
    [queryClient],
  );

  const login = useCallback(
    async (input: LoginInput) => adopt(await authApi.login(input)),
    [adopt],
  );

  const register = useCallback(
    // Server cấp phiên ngay trong lời gọi đăng ký, nên không có bước "đăng ký
    // xong rồi đăng nhập lại" — và cũng không có khoảnh khắc nào người dùng
    // vừa có tài khoản mà vẫn đứng ngoài.
    async (input: RegisterInput) => adopt(await authApi.register(input)),
    [adopt],
  );

  const loginAsDemo = useCallback(
    async (userId: string) => adopt(await authApi.demoLogin({ userId })),
    [adopt],
  );

  const loginWithTelegram = useCallback(
    async (initData: string) => adopt(await authApi.telegramMiniApp({ initData })),
    [adopt],
  );

  /**
   * Hoàn tất đăng ký sau khi Google xác minh email — APM-GAUTH-a.
   *
   * Cùng khuôn với `register`: server mở phiên ngay trong lời gọi, nên không có
   * khoảnh khắc nào người dùng vừa có tài khoản mà vẫn đứng ngoài.
   */
  const completeOnboarding = useCallback(
    async (input: CompleteOnboardingInput) => {
      const result = await authApi.completeOnboarding(input);
      if ('status' in result) return result;
      adopt(result);
      return null;
    },
    [adopt],
  );

  const enterFromOnboarding = useCallback(
    async () => adopt(await authApi.enterFromOnboarding()),
    [adopt],
  );

  const logout = useCallback(async () => {
    // Đặt trạng thái trước khi gọi API: người dùng bấm đăng xuất thì phải ra
    // ngay, không phụ thuộc việc server có trả lời hay không.
    setStatus('anonymous');
    setUser(null);
    setExpired(false);
    tokenStore.set(null);
    /*
      Dọn cache khi ra: để lại thì người đăng nhập kế tiếp trên cùng trình duyệt
      thấy dự án, việc và báo cáo của người vừa đăng xuất trong khoảnh khắc
      trước khi query mới trả lời.
    */
    queryClient.clear();

    try {
      await authApi.logout();
    } catch {
      // Đăng xuất mà lỗi mạng thì phía client vẫn phải sạch. Refresh token còn
      // sống ở server sẽ hết hạn theo lịch của nó.
    }
  }, [queryClient]);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      user,
      login,
      register,
      loginAsDemo,
      loginWithTelegram,
      completeOnboarding,
      enterFromOnboarding,
      logout,
      expired,
    }),
    [
      status,
      user,
      login,
      register,
      loginAsDemo,
      loginWithTelegram,
      completeOnboarding,
      enterFromOnboarding,
      logout,
      expired,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth() phải nằm trong <AuthProvider>');
  return value;
}

/**
 * Id người đang đăng nhập, hoặc `null` — **không ném** khi thiếu `AuthProvider`.
 * Cho tuỳ chọn cá nhân lưu trên trình duyệt (lề tài liệu, APM-MYF-h): máy dùng
 * chung thì mỗi tài khoản giữ lựa chọn của mình.
 */
export function useCurrentUserId(): string | null {
  return useContext(AuthContext)?.user?.id ?? null;
}

/**
 * Người đang đăng nhập, hoặc `null` — **không ném** khi thiếu `AuthProvider`.
 * Cho chỗ chỉ cần tên để điền sẵn (người trình bày trên slide tiêu đề, APM-SLD-k)
 * và được dựng cả trong test không có phiên.
 */
export function useOptionalUser(): AuthenticatedUser | null {
  return useContext(AuthContext)?.user ?? null;
}

/** Chỉ dùng trong test — cho phép mỗi test case khôi phục phiên lại từ đầu. */
export function resetBootRestore(): void {
  bootRestore = null;
}
